import { Section, SectionParagraph } from '@/components/shared/Content';
import { Box, Flex } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import type React from 'react';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { CollapsibleBox, CollapsibleBoxContainer } from '../shared/CollapsibleBox';
import JoinCTA from '../shared/JoinCTA';
import { Subtitle, Title } from './shared/ContentStyling';

const CTA = styled(JoinCTA)`
  padding-inline: 29px;
`;

export interface faqDataProps {
  question: string;
  answer: string;
  analytics: string;
}

const faqData: faqDataProps[] = [
  {
    question: 'What is renters insurance?',
    answer: `Renters insurance is a safety net for tenants, providing help if unfortunate incidents affect you or your belongings. Your policy can step in when there's loss or damage due to specified "perils," including theft, fire, vandalism, certain types of water damage, and more, as outlined in your policy. For more details, check out our article on <a href="https://www.lemonade.com/renters/explained/what-is-renters-insurance/" target="_blank">"What is renters insurance?"</a>.`,
    analytics: 'renters_what_is_renters_insurance',
  },
  {
    question: 'What does Lemonade Renters insurance cover?',
    answer: `Your policy covers your personal property against theft and damage, plus legal claims for damage or injury you accidentally cause others. Also, if your home becomes unlivable due to certain types of damage we’ll help cover the costs of temporary living arrangements. Learn more about <a href="https://www.lemonade.com/renters/explained/what-is-renters-insurance-and-whats-covered/" target="_blank">what's covered</a>.`,
    analytics: 'renters_what_does_the_policy_cover',
  },
  {
    question: 'Does Lemonade offer renters insurance near me?',
    answer: `We're glad you asked! Lemonade's renters insurance is available in 28 states plus Washington, D.C., and we're still growing. <a href="https://www.lemonade.com/lemonade-goes-global" target="_blank">Check out the availability of our renters insurance</a> and other products.`,
    analytics: 'renters_what_is_not_covered',
  },
  {
    question: 'What if something I own gets stolen outside of my home?',
    answer: `You're still covered! Your Lemonade renters policy covers your personal property anywhere in the world.
                <a href="https://www.lemonade.com/renters/explained/does-renters-insurance-cover-theft/" target="_blank">Read all about theft coverage here</a>. `,
    analytics: 'renters_stolen_outside',
  },
  {
    question: 'What if I want to cancel my policy?',
    answer:
      'You can cancel your policy at any time via the Lemonade app or website, and you’ll receive a refund for the remaining period you’ve already paid for. No hard feelings!',
    analytics: 'renters_policy_cancelation',
  },
  {
    question: 'How much does renters\ninsurance cost?',
    answer: `Our affordable renters insurance coverage starts at $5 per month, but your <a href="https://www.lemonade.com/renters/explained/this-is-how-much-renters-insurance-actually-costs/." target="_blank">policy price</a> (aka the ‘premium’) will be affected by the coverage amounts you choose, among other factors. <a href="https://www.lemonade.com/onboarding/start?p=home_us_renters" target="_blank">You can get your online renters insurance quote here.</a>`,
    analytics: 'renters_how_much_does_it_cost',
  },
  {
    question: 'How do I file a claim?',
    answer:
      'Very easily: just open the Lemonade app and hit the ‘Claim’ button. We’ll guide you through the process from there.',
    analytics: 'renters_how_to_file_a_claim',
  },
  {
    question: 'I already have a policy with another carrier - can I switch?',
    answer:
      'Sure you can! Once you sign up with us, we’ll send your previous insurer a formal notice. Once they get it, they should cancel the remainder of your policy term and refund you for any outstanding payments.',
    analytics: 'renters_how_can_i_switch',
  },
  {
    question: 'How do I add an interested party?',
    answer: `All you need to do is tap ‘Add-ons’ under your policy page in the <a href="https://www.lemonade.com/download" target="_blank">Lemonade app</a> or your policy on <a href="https://me.lemonade.com/" target="_blank">your account page here</a> and select ‘Interested Party’. Fill in their name, address, and email and you’re all set!`,
    analytics: 'renters_how_to_add_interested_party',
  },
  {
    question: 'What is Extra Coverage?',
    answer:
      'Extra Coverage offers enhanced protection for jewelry, fine art, cameras, bicycles, and musical instruments, with deductible-free claims and coverage in cases of accidental loss and damage. You can apply for Extra Coverage when you sign up for a policy, or at any time after you’ve activated your policy.',
    analytics: 'what_is_extra_coverage',
  },
  {
    question: 'What bundles and discounts does Lemonade Renters offer?',
    answer: `In addition to renters insurance, Lemonade offers homeowners, car, pet, and term life policies. You can unlock an automatic bundle discount with any combination, <a href="https://www.lemonade.com/renters/explained/renters-and-car-insurance-bundle/" target=“_blank”>like Car and Renters</a>, so it’s both a great way to consolidate your insurance needs and an opportunity to save. You could also score a discount on your Renters policy if your place includes protective devices, like deadbolts, smoke alarms, and burglar alarms.`,
    analytics: 'what_bundles_and_discounts',
  },
  {
    question: 'Who needs renters insurance?',
    answer:
      'All renters should get renters insurance, since it offers vital financial protection at a very low cost. Your landlord or property manager <a href="https://www.lemonade.com/renters/explained/why-do-landlords-require-renters-insurance/" target="_blank">may require renters insurance </a>, and getting a renters policy can save you thousands for damages caused by things like fires, theft, vandalism, burst pipes, windstorms, and more.',
    analytics: 'what_is_extra_coverage',
  },
];

export const SectionWrapperInner = styled(Flex).attrs({
  justifyContent: 'center',
  flexDirection: 'column',
})`
  ${MEDIA_QUERIES.tabletAndDesktop`
        max-width: 1144px;
        width: 100%;
    `}
`;
export interface FaqProps {
  title: string;
  subtitle: string;
}

export const Faq: React.FC<FaqProps> = ({ title, subtitle }) => {
  const { trackEvent } = useAnalyticsContext();
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  useEffect(() => {
    if (inView) {
      trackEvent('renters_faq_section.viewed');
    }
  }, [inView, trackEvent]);

  const faqTrackEvent = ({ eventName, analytics }: { eventName: string; analytics: string }) =>
    trackEvent(eventName, {
      product: 'renters',
      analytics,
    });

  return (
    <Section pb={[40, 70]} pt={[40, 70]} px={20} darkBackground ref={ref}>
      <Title tabIndex={0}>{parse(title)}</Title>
      <Subtitle px={[20, 0, 0]} mb={65} pt={20} textAlign="center">
        {parse(subtitle)}
      </Subtitle>
      <SectionWrapperInner>
        <CollapsibleBoxContainer minBoxesHeight="325px">
          {faqData.map(({ question, answer, analytics }) => (
            <CollapsibleBox
              key={question}
              title={question}
              content={answer}
              onMouseEnter={() =>
                faqTrackEvent({
                  eventName: 'faq.hovered',
                  analytics,
                })
              }
              onClick={() =>
                faqTrackEvent({
                  eventName: 'faq.clicked',
                  analytics,
                })
              }
            />
          ))}
        </CollapsibleBoxContainer>
        <Flex mt="40px" mb="20px" gap="40px" flexDirection="column" justifyContent="center" alignItems="center">
          <SectionParagraph>Didn&#39;t find what you were looking for?</SectionParagraph>
          <Box>
            <CTA
              size="small"
              flavor="standard"
              customUrl="/renters/explained/lemonade-renters-and-homeowners-faq/."
              data-event="renters_visit_faq_page.clicked"
              aria-label="Didn't find what you were looking for? Visit our FAQ"
            >
              Visit our FAQ
            </CTA>
          </Box>
        </Flex>
      </SectionWrapperInner>
    </Section>
  );
};
